/**
 * Servicio para gestión de permisos
 * Sistema Klever Octopus - Betel Natural
 */
import apiClient, { handleApiError, formatApiResponse } from './api';

// Endpoints para permisos
const ENDPOINTS = {
  PERMISSIONS: '/permissions', // Endpoint RBAC (cambiado de /users/permissions a /permissions)
  PERMISSION_BY_ID: (id) => `/permissions/${id}`,
  PERMISSION_ROLES: (id) => `/permissions/${id}/roles`
};

/**
 * Obtener lista de permisos (la API devuelve todos los permisos organizados por rol)
 */
export const getPermissions = async (params = {}) => {
  try {
    const response = await apiClient.get(ENDPOINTS.PERMISSIONS);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener todos los permisos (sin paginación)
 */
export const getAllPermissions = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.PERMISSIONS);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener permiso por ID
 */
export const getPermissionById = async (id) => {
  try {
    const response = await apiClient.get(ENDPOINTS.PERMISSION_BY_ID(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Crear nuevo permiso
 */
export const createPermission = async (permissionData) => {
  try {
    const response = await apiClient.post(ENDPOINTS.PERMISSIONS, {
      name: permissionData.name,
      display_name: permissionData.display_name,
      description: permissionData.description || null,
      module: permissionData.module || 'system',
      group: permissionData.group || 'general',
      is_system: permissionData.is_system || false,
      is_active: permissionData.is_active !== undefined ? permissionData.is_active : true
    });
    
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Actualizar permiso
 */
export const updatePermission = async (id, permissionData) => {
  try {
    const response = await apiClient.put(ENDPOINTS.PERMISSION_BY_ID(id), {
      name: permissionData.name,
      display_name: permissionData.display_name,
      description: permissionData.description,
      module: permissionData.module,
      group: permissionData.group,
      is_active: permissionData.is_active
    });
    
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Eliminar permiso (solo permisos personalizados)
 */
export const deletePermission = async (id) => {
  try {
    const response = await apiClient.delete(ENDPOINTS.PERMISSION_BY_ID(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener roles que tienen un permiso específico
 */
export const getPermissionRoles = async (id) => {
  try {
    const response = await apiClient.get(ENDPOINTS.PERMISSION_ROLES(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Sincronizar permisos del sistema
 */
export const syncSystemPermissions = async () => {
  try {
    const response = await apiClient.post(ENDPOINTS.SYNC_PERMISSIONS);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener módulos disponibles
 */
export const getModules = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.MODULES);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener grupos disponibles por módulo
 */
export const getGroups = async (module = null) => {
  try {
    const url = module ? `${ENDPOINTS.GROUPS}?module=${module}` : ENDPOINTS.GROUPS;
    const response = await apiClient.get(url);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Validar nombre de permiso único
 */
export const validateUniquePermissionName = async (name, excludePermissionId = null) => {
  try {
    const response = await apiClient.post('/permisos/validar-nombre', {
      name: name,
      exclude_permission_id: excludePermissionId
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener estadísticas de permisos
 */
export const getPermissionStats = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.PERMISSION_STATS);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Exportar configuración de permisos
 */
export const exportPermissions = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      format: params.format || 'json',
      include_roles: params.include_roles || false,
      module: params.module || '',
      permission_ids: params.permission_ids ? params.permission_ids.join(',') : ''
    });
    
    const response = await apiClient.get(`/permisos/exportar?${queryParams}`, {
      responseType: 'blob'
    });
    
    return { success: true, data: response.data };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Importar configuración de permisos
 */
export const importPermissions = async (file, options = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('merge_strategy', options.merge_strategy || 'skip');
    formData.append('update_existing', options.update_existing || false);
    
    const response = await apiClient.post('/permisos/importar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener permisos agrupados por módulo
 */
export const getPermissionsByModule = async () => {
  try {
    const response = await apiClient.get('/permissions/por-modulo');
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Verificar si un permiso está siendo usado
 */
export const checkPermissionUsage = async (id) => {
  try {
    const response = await apiClient.get(`${ENDPOINTS.PERMISSION_BY_ID(id)}/uso`);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Crear múltiples permisos en lote
 */
export const createBulkPermissions = async (permissionsData) => {
  try {
    const response = await apiClient.post('/permisos/crear-lote', {
      permissions: permissionsData
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export default {
  getPermissions,
  getAllPermissions,
  getPermissionById,
  createPermission,
  updatePermission,
  deletePermission,
  getPermissionRoles,
  syncSystemPermissions,
  getModules,
  getGroups,
  validateUniquePermissionName,
  getPermissionStats,
  exportPermissions,
  importPermissions,
  getPermissionsByModule,
  checkPermissionUsage,
  createBulkPermissions
};
